import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { LoaderCircle, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { submittalStatusVariant } from '@/lib/status-variants'
import { cn } from '@/lib/utils'
import * as Query from '../../../api/gql/query'
import { pickableSubmittals, type SubmittalOption } from '../lib/integrations'

/** Every status but VOID: a void submittal cannot be linked (spec §2.2). */
const PICKABLE_STATUSES = ['DRAFT', 'OPEN', 'UNDER_REVIEW', 'RETURNED_FOR_REVISION', 'APPROVED', 'APPROVED_AS_NOTED', 'REJECTED', 'CLOSED']

interface Props {
  open: boolean
  projectId: string
  contractId: string
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server linked them. */
  onLink: (ids: string[]) => Promise<boolean>
}

const SubmittalPickerDialog: React.FC<Props> = ({ open, projectId, contractId, onOpenChange, onLink }) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(id)
  }, [search])
  useEffect(() => {
    if (open) return
    setSearch('')
    setDebounced('')
    setPicked([])
  }, [open])
  const { data, loading } = useQuery<{ submittals: { items: SubmittalOption[] } }>(Query.COST_SUBMITTAL_OPTIONS, {
    variables: { projectId, filter: { search: debounced || undefined, status: PICKABLE_STATUSES }, limit: 50 },
    skip: !open,
    fetchPolicy: 'network-only',
    context: { handlesErrors: true },
  })
  const rows = pickableSubmittals(data?.submittals.items ?? [], contractId)
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))
  const link = async () => {
    setSaving(true)
    const ok = await onLink(picked)
    setSaving(false)
    if (ok) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('CostControl_submittals_pick_title')}</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('CostControl_submittals_search')} className="h-9 pl-8" />
        </div>
        <div className="max-h-80 space-y-1 overflow-y-auto">
          {loading && !data && <LoaderCircle className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />}
          {!loading && rows.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">{t('CostControl_submittals_none_found')}</p>}
          {rows.map((r) => (
            <label key={r.id} className={cn('flex items-center gap-2 rounded px-2 py-1.5 text-sm', r.linkedTo ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-muted')}>
              <Checkbox checked={picked.includes(r.id)} disabled={!!r.linkedTo} onCheckedChange={() => toggle(r.id)} />
              <span className="min-w-0 flex-1 truncate">{r.label}</span>
              {r.linkedTo && <span className="shrink-0 text-xs text-muted-foreground">{t('CostControl_submittals_linked_elsewhere', { label: r.linkedTo })}</span>}
              <Badge variant={submittalStatusVariant(r.status)} className="shrink-0">{t(`Submittal_status_${r.status}`)}</Badge>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('Common_cancel')}</Button>
          <Button onClick={link} disabled={!picked.length || saving}>
            {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('CostControl_submittals_pick_confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default SubmittalPickerDialog
